/**
 * Выбор адреса из справочника биллинга — каскадом город → улица → дом →
 * квартира, без ручного ввода строки. Списки едут вживую из биллинга (см.
 * api.addressCities/Streets/Houses), поэтому выбранный адрес заведомо совпадает
 * с тем, что оператор потом увидит при заведении договора.
 *
 * Используется дважды: для адреса подключения услуги и для адреса регистрации
 * (прописки) — отсюда параметр `label`. Прописка чаще всего не выбирается
 * руками, а подставляется из карточки договора по логину, и тогда сюда приходит
 * готовое значение с id — каскад остаётся свёрнутым.
 *
 * Дом, которого нет в справочнике, монтажник заводит сам (кнопка в списке
 * домов). Улицу — нет: если её нет, монтажник оставляет заявку оператору, а
 * адрес до её заведения идёт текстом (город + название улицы + дом), с
 * привязкой только к городу. Так заявку можно дозаполнить, не дожидаясь
 * оператора в поле.
 *
 * Наружу отдаётся AddressValue: id города/улицы/дома (любой может быть null),
 * квартира отдельным полем — как в биллинге, где дом справочный, а квартира
 * произвольная, — плюс готовая строка для печати в договоре.
 */

import { useEffect, useRef, useState } from 'react'

import { ApiError, api } from '../api/client'
import type { AddressItem, HouseItem } from '../api/types'
import { Alert, Button } from './ui'

export interface AddressValue {
  city_id: number | null
  street_id: number | null
  house_id: number | null
  /** Квартира — хранится отдельно от дома, как в биллинге. */
  flat: string
  /** Человекочитаемая строка адреса — уходит в connection_address. */
  text: string
}

export const EMPTY_ADDRESS: AddressValue = {
  city_id: null,
  street_id: null,
  house_id: null,
  flat: '',
  text: '',
}

interface ComboOption {
  key: string
  label: string
}

// ── общий асинхронный комбобокс ──────────────────────────────────────────────

interface ComboboxProps {
  label: string
  required?: boolean
  disabled?: boolean
  error?: string
  hint?: string
  placeholder?: string
  /** Текст уже выбранного значения (показывается, пока список закрыт). */
  selectedLabel?: string
  search: (query: string, signal: AbortSignal) => Promise<ComboOption[]>
  onSelect: (option: ComboOption) => void
  /** Ключ, при смене которого список сбрасывается (например, выбран другой город). */
  resetKey?: string | number
  /** Подвал списка, когда ничего не нашлось — «завести дом», «нет улицы». */
  emptyAction?: (query: string) => React.ReactNode
}

function Combobox({
  label,
  required,
  disabled,
  error,
  hint,
  placeholder = 'Начните вводить…',
  selectedLabel,
  search,
  onSelect,
  resetKey,
  emptyAction,
}: ComboboxProps) {
  const [query, setQuery] = useState('')
  const [open, setOpen] = useState(false)
  const [options, setOptions] = useState<ComboOption[]>([])
  const [loading, setLoading] = useState(false)
  const [failed, setFailed] = useState(false)
  const boxRef = useRef<HTMLDivElement>(null)

  // Смена города/улицы сбрасывает нижние уровни.
  useEffect(() => {
    setQuery('')
    setOptions([])
    setOpen(false)
  }, [resetKey])

  // Поиск с задержкой: не бомбим биллинг на каждую букву. Устаревший запрос
  // отменяется — иначе поздний ответ перетрёт свежий.
  useEffect(() => {
    if (!open) return
    const controller = new AbortController()
    const handle = window.setTimeout(async () => {
      setLoading(true)
      setFailed(false)
      try {
        setOptions(await search(query, controller.signal))
      } catch (err) {
        if (err instanceof DOMException && err.name === 'AbortError') return
        setFailed(true)
        setOptions([])
      } finally {
        setLoading(false)
      }
    }, 250)
    return () => {
      controller.abort()
      window.clearTimeout(handle)
    }
  }, [query, open, search])

  // Клик вне — закрыть список.
  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [open])

  const classes = ['field', 'combobox']
  if (error) classes.push('invalid')

  const pick = (option: ComboOption) => {
    onSelect(option)
    setQuery('')
    setOpen(false)
  }

  return (
    <div className={classes.join(' ')} ref={boxRef}>
      <label>
        {label}
        {required && <span className="required"> *</span>}
      </label>

      {selectedLabel && !open ? (
        <button
          type="button"
          className="combo-selected"
          disabled={disabled}
          onClick={() => setOpen(true)}
        >
          <span>{selectedLabel}</span>
          <span className="combo-change">изменить</span>
        </button>
      ) : (
        <div className="combo-input-wrap">
          <input
            type="text"
            value={query}
            disabled={disabled}
            placeholder={placeholder}
            autoComplete="off"
            onFocus={() => setOpen(true)}
            onChange={(e) => {
              setQuery(e.target.value)
              setOpen(true)
            }}
          />
        </div>
      )}

      {open && !disabled && (
        <div className="combo-list">
          {loading && <div className="combo-msg">Поиск…</div>}
          {!loading && failed && (
            <div className="combo-msg combo-error">Справочник недоступен — повторите</div>
          )}
          {!loading && !failed && options.length === 0 && (
            <div className="combo-msg">
              {query.trim() ? 'Ничего не найдено' : 'Начните вводить название'}
            </div>
          )}
          {!loading &&
            options.map((option) => (
              <button
                type="button"
                key={option.key}
                className="combo-option"
                onClick={() => pick(option)}
              >
                {option.label}
              </button>
            ))}
          {!loading && !failed && emptyAction && query.trim() && (
            <div className="combo-foot">{emptyAction(query.trim())}</div>
          )}
        </div>
      )}

      {(error || hint) && <div className="hint">{error ?? hint}</div>}
    </div>
  )
}

// ── каскад адреса ────────────────────────────────────────────────────────────

interface Props {
  value: AddressValue
  onChange: (value: AddressValue) => void
  error?: string
  /** Подпись свёрнутого вида: «Адрес подключения», «Адрес регистрации». */
  label?: string
  /** Подсказка под свёрнутым адресом — например, откуда он взялся. */
  hint?: string
}

interface Picked {
  city: AddressItem | null
  street: AddressItem | null
  house: HouseItem | null
}

function compose(
  city: AddressItem | null,
  streetLabel: string,
  houseNumber: string,
  index = '',
  flat = '',
): string {
  const parts = [
    index || null,
    city ? `${city.type} ${city.title}`.trim() : null,
    streetLabel || null,
    houseNumber ? `д. ${houseNumber}` : null,
    flat.trim() ? `кв. ${flat.trim()}` : null,
  ].filter(Boolean)
  return parts.join(', ')
}

export function AddressPicker({
  value,
  onChange,
  error,
  label = 'Адрес подключения',
  hint,
}: Props) {
  const [picked, setPicked] = useState<Picked>({ city: null, street: null, house: null })
  // Режим «улицы нет в справочнике»: улица и дом вводятся текстом, привязка —
  // только к городу. Включается после отправки заявки на улицу.
  const [manualStreet, setManualStreet] = useState('')
  const [manualHouse, setManualHouse] = useState('')
  const [flat, setFlat] = useState(value.flat)
  const [busy, setBusy] = useState(false)
  const [notice, setNotice] = useState<string | null>(null)
  const [failure, setFailure] = useState<string | null>(null)

  // Уже сохранённый адрес (открыли готовую заявку): показываем строкой с
  // кнопкой «изменить», не заставляя проходить каскад заново.
  const [editing, setEditing] = useState(!value.text)

  // Ручной ввод: адреса нет в справочнике — монтажник вписывает строкой. Так
  // сохранённый ранее текстовый адрес (без города) и открывается сразу текстом.
  const [manual, setManual] = useState(Boolean(value.text && !value.city_id && !value.street_id))
  const [manualText, setManualText] = useState(value.text)

  const goManual = () => {
    setManualText(value.text || '')
    setManual(true)
    // Переходя в ручной ввод, сбрасываем привязку к справочнику — остаётся строка.
    onChange({ city_id: null, street_id: null, house_id: null, flat: '', text: value.text || '' })
  }

  const emit = (
    next: Picked,
    manStreet = manualStreet,
    manHouse = manualHouse,
    apartment = flat,
  ) => {
    if (next.city && next.street && next.house) {
      onChange({
        city_id: next.city.id,
        street_id: next.street.id,
        house_id: next.house.id,
        flat: apartment.trim(),
        text: compose(
          next.city,
          next.street.label,
          next.house.number,
          next.house.index,
          apartment,
        ),
      })
    } else if (next.city && manStreet) {
      // Улицы нет в справочнике — только город плюс текст.
      onChange({
        city_id: next.city.id,
        street_id: null,
        house_id: null,
        flat: apartment.trim(),
        text: compose(next.city, manStreet, manHouse, '', apartment),
      })
    } else {
      onChange(EMPTY_ADDRESS)
    }
  }

  const selectCity = (option: ComboOption) => {
    const city: AddressItem = { id: Number(option.key), title: option.label, type: '', label: option.label }
    const next = { city, street: null, house: null }
    setPicked(next)
    setManualStreet('')
    setManualHouse('')
    emit(next, '', '')
  }

  const selectStreet = (option: ComboOption) => {
    const street: AddressItem = { id: Number(option.key), title: option.label, type: '', label: option.label }
    const next = { ...picked, street, house: null }
    setPicked(next)
    setManualStreet('')
    emit(next)
  }

  const selectHouse = (item: HouseItem) => {
    const next = { ...picked, house: item }
    setPicked(next)
    emit(next)
  }

  const createHouse = async (raw: string) => {
    if (!picked.street) return
    setBusy(true)
    setFailure(null)
    try {
      const item = await api.createHouse(picked.street.id, raw)
      selectHouse(item)
      setNotice(`Дом ${item.number} добавлен в справочник`)
    } catch (err) {
      setFailure(err instanceof ApiError ? err.message : 'Не удалось завести дом')
    } finally {
      setBusy(false)
    }
  }

  const requestStreet = async (title: string) => {
    if (!picked.city) return
    setBusy(true)
    setFailure(null)
    try {
      await api.createStreetRequest({
        city_id: picked.city.id,
        city_title: picked.city.label,
        street_title: title,
      })
      setManualStreet(title)
      setNotice(
        `Заявка на улицу «${title}» отправлена оператору. Пока введите улицу и дом текстом — ` +
          `оператор привяжет адрес к справочнику позже.`,
      )
      emit({ ...picked, street: null, house: null }, title, manualHouse)
    } catch (err) {
      setFailure(err instanceof ApiError ? err.message : 'Не удалось отправить заявку')
    } finally {
      setBusy(false)
    }
  }

  // Готовый адрес, каскад свёрнут.
  if (!editing) {
    return (
      <div className="field">
        <label>{label}</label>
        <div className="address-summary">
          <span>{value.text}</span>
          <Button variant="ghost" onClick={() => setEditing(true)}>
            Изменить
          </Button>
        </div>
        {value.city_id && !value.street_id && (
          <div className="hint">
            Улица вне справочника — по ней отправлена заявка оператору.
          </div>
        )}
        {!value.city_id && (
          <div className="hint">
            Адрес вне справочника биллинга — записан строкой. Чтобы привязать его
            к дому, нажмите «Изменить».
          </div>
        )}
        {hint && <div className="hint">{hint}</div>}
      </div>
    )
  }

  return (
    <div className="address-picker">
      {notice && (
        <Alert kind="ok" onDismiss={() => setNotice(null)}>
          {notice}
        </Alert>
      )}
      {failure && (
        <Alert kind="error" title="Не получилось" onDismiss={() => setFailure(null)}>
          {failure}
        </Alert>
      )}

      <div className="address-picker-title">{label}</div>

      {manual ? (
        <div className="field">
          <input
            type="text"
            value={manualText}
            placeholder="Город, улица, дом, квартира — если адреса нет в списке"
            autoComplete="off"
            onChange={(e) => {
              setManualText(e.target.value)
              onChange({
                city_id: null,
                street_id: null,
                house_id: null,
                flat: '',
                text: e.target.value,
              })
            }}
          />
          <div className="hint">
            Записываем строкой, без привязки к справочнику.{' '}
            <button type="button" className="linklike" onClick={() => setManual(false)}>
              выбрать из справочника
            </button>
          </div>
        </div>
      ) : (
        <>
      <Combobox
        label="Город / населённый пункт"
        required
        placeholder="Например: Якутск"
        selectedLabel={picked.city?.label}
        search={async (q, signal) =>
          (await api.addressCities(q, signal)).map((c) => ({ key: String(c.id), label: c.label }))
        }
        onSelect={selectCity}
      />

      {/* Улица: обычный выбор из справочника, либо режим текста после заявки. */}
      {manualStreet ? (
        <div className="field">
          <label>
            Улица<span className="required"> *</span>
          </label>
          <input
            type="text"
            value={manualStreet}
            onChange={(e) => {
              setManualStreet(e.target.value)
              emit(picked, e.target.value, manualHouse)
            }}
          />
          <div className="hint">Заявка отправлена — улица будет привязана к справочнику позже</div>
        </div>
      ) : (
        <Combobox
          label="Улица"
          required
          disabled={!picked.city}
          placeholder={picked.city ? 'Например: Ленина' : 'Сначала выберите город'}
          selectedLabel={picked.street?.label}
          resetKey={picked.city?.id ?? 'none'}
          search={async (q, signal) =>
            picked.city
              ? (await api.addressStreets(picked.city.id, q, signal)).map((s) => ({
                  key: String(s.id),
                  label: s.label,
                }))
              : []
          }
          onSelect={selectStreet}
          emptyAction={(q) => (
            <Button variant="ghost" busy={busy} onClick={() => requestStreet(q)}>
              Улицы нет — оставить заявку на «{q}»
            </Button>
          )}
        />
      )}

      {/* Дом: выбор из справочника, либо текст в режиме заявки на улицу. */}
      {manualStreet ? (
        <div className="field">
          <label>Дом</label>
          <input
            type="text"
            value={manualHouse}
            placeholder="Например: 12 или 12А"
            onChange={(e) => {
              setManualHouse(e.target.value)
              emit(picked, manualStreet, e.target.value)
            }}
          />
        </div>
      ) : (
        <HouseCombobox
          street={picked.street}
          selected={picked.house}
          onSelect={selectHouse}
          onCreate={createHouse}
          busy={busy}
        />
      )}

      {/* Квартира в справочнике не заводится — это свободный ввод, как и в
          биллинге (contract_parameter_type_2.flat). */}
      <div className="field">
        <label htmlFor={`${label}-flat`}>Квартира</label>
        <input
          id={`${label}-flat`}
          type="text"
          value={flat}
          placeholder="Например: 15"
          autoComplete="off"
          onChange={(e) => {
            setFlat(e.target.value)
            emit(picked, manualStreet, manualHouse, e.target.value)
          }}
        />
        <div className="hint">Частный дом — оставьте пустым</div>
      </div>

          <div className="address-manual-toggle">
            <button type="button" className="linklike" onClick={goManual}>
              Адреса нет в списке? Ввести вручную
            </button>
          </div>
        </>
      )}

      {error && <div className="field invalid"><div className="hint">{error}</div></div>}
    </div>
  )
}

// Дом хранит не только label, но и индекс/номер — держим полный объект.
function HouseCombobox({
  street,
  selected,
  onSelect,
  onCreate,
  busy,
}: {
  street: AddressItem | null
  selected: HouseItem | null
  onSelect: (item: HouseItem) => void
  onCreate: (raw: string) => void
  busy: boolean
}) {
  const cache = useRef<Map<string, HouseItem>>(new Map())
  return (
    <Combobox
      label="Дом"
      disabled={!street}
      placeholder={street ? 'Номер дома' : 'Сначала выберите улицу'}
      selectedLabel={selected ? `д. ${selected.number}${selected.index ? ` (индекс ${selected.index})` : ''}` : undefined}
      resetKey={street?.id ?? 'none'}
      search={async (q, signal) => {
        if (!street) return []
        const houses = await api.addressHouses(street.id, q, signal)
        cache.current = new Map(houses.map((h) => [String(h.id), h]))
        return houses.map((h) => ({
          key: String(h.id),
          label: h.index ? `д. ${h.number} — ${h.index}` : `д. ${h.number}`,
        }))
      }}
      onSelect={(option) => {
        const item = cache.current.get(option.key)
        if (item) onSelect(item)
      }}
      emptyAction={(q) => (
        <Button variant="ghost" busy={busy} onClick={() => onCreate(q)}>
          Завести дом «{q}» в справочнике
        </Button>
      )}
    />
  )
}
